import React from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@src/lib/ui/Chart";
import { gql } from "@apollo/client";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { cn } from "@src/lib/utils/cn";
import { addWeeks, format, startOfWeek, subDays } from "date-fns";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import { useGuestFeedbackRatingTrendQuery } from "./GuestFeedbackRatingTrendChart.generated";
import { useRecommendationsMockData } from "@src/features/recommendations/RecommendationsMockDataContext";
import { getMockGuestFeedbackRatingTrend } from "./recommendationsMockData";

const CHART_CONFIG: ChartConfig = {
  avgRating: {
    label: "Avg Rating",
    color: "#4778F5",
  },
  count: {
    label: "Mentions",
    color: "#7c3aed",
  },
};

// `days: 0` means all time — we omit the startDate so the resolver returns the
// full history rather than a rolling window.
const ALL_TIME_DAYS = 0;
const RANGE_OPTIONS = [
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
  { label: "All time", days: ALL_TIME_DAYS },
] as const;

// Week-start values come back as date-only strings (YYYY-MM-DD). Parse them in
// local time so week bucketing lines up with the user's calendar, not UTC.
function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

type Props = {
  compact?: boolean;
  themeId?: string | null;
  locationIds?: number[];
};

const GuestFeedbackRatingTrendChart: React.FC<Props> = ({
  compact = false,
  themeId,
  locationIds,
}) => {
  // Reviews trend by default; switch to the per-theme mention trend once a
  // theme is selected.
  const showMentions = !!themeId;
  const [rangeDays, setRangeDays] = useQueryParam(
    "trendRange",
    withDefault(NumberParam, 90),
  );
  const chartHeight = compact ? "h-[160px]" : "h-[250px]";
  const containerSpacing = compact
    ? "flex flex-col gap-3"
    : "flex flex-col gap-6 py-4";

  const startDate = React.useMemo(
    () =>
      rangeDays === ALL_TIME_DAYS
        ? undefined
        : subDays(new Date(), rangeDays).toISOString(),
    [rangeDays],
  );

  const { useMockData } = useRecommendationsMockData();
  const { data, loading: queryLoading } = useGuestFeedbackRatingTrendQuery({
    variables: {
      input: {
        startDate,
        themeId: themeId || undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
      },
    },
    skip: useMockData,
  });

  const loading = queryLoading && !useMockData;
  const points = React.useMemo(
    () =>
      useMockData
        ? getMockGuestFeedbackRatingTrend({ startDate, themeId, locationIds })
        : data?.GuestFeedbackRatingTrend ?? [],
    [data, useMockData, startDate, themeId, locationIds],
  );
  const hasData = points.length > 0;

  const isRangeLimited = rangeDays !== ALL_TIME_DAYS;
  const { data: allTimeData, loading: allTimeQueryLoading } =
    useGuestFeedbackRatingTrendQuery({
      variables: {
        input: {
          themeId: themeId || undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
        },
      },
      skip: useMockData || loading || hasData || !isRangeLimited,
    });
  const allTimePoints = React.useMemo(
    () =>
      useMockData
        ? getMockGuestFeedbackRatingTrend({ themeId, locationIds })
        : allTimeData?.GuestFeedbackRatingTrend ?? [],
    [allTimeData, useMockData, themeId, locationIds],
  );
  const hasDataOutsideRange =
    !hasData && isRangeLimited && allTimePoints.length > 0;
  const resolvingEmptyState =
    !hasData && isRangeLimited && allTimeQueryLoading && !useMockData;

  // Build a continuous weekly series so the X-axis always runs through to the
  // current week, even when the most recent review is well in the past. Weeks
  // with no feedback are padded (null rating / zero mentions); the line itself
  // still stops at the last real data point.
  const formattedData = React.useMemo(() => {
    if (points.length === 0) return [];
    const byWeek = new Map<number, { avgRating: number; count: number }>();
    for (const point of points) {
      const week = startOfWeek(parseLocalDate(point.weekStart), {
        weekStartsOn: 1,
      });
      byWeek.set(week.getTime(), {
        avgRating: point.avgRating,
        count: point.count,
      });
    }

    const endWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    const startWeek =
      rangeDays === ALL_TIME_DAYS
        ? new Date(Math.min(...byWeek.keys()))
        : startOfWeek(subDays(new Date(), rangeDays), { weekStartsOn: 1 });

    // Key each point by its full date, not a display label: "MMM d" labels
    // repeat across years, and a category X-axis collapses duplicate values
    // onto one position, making the line double back on itself on "All time".
    const series: {
      weekKey: string;
      avgRating: number | null;
      count: number;
    }[] = [];
    for (
      let week = startWeek;
      week.getTime() <= endWeek.getTime();
      week = addWeeks(week, 1)
    ) {
      const hit = byWeek.get(week.getTime());
      series.push({
        weekKey: format(week, "yyyy-MM-dd"),
        avgRating: hit ? hit.avgRating : null,
        count: hit ? hit.count : 0,
      });
    }
    return series;
  }, [points, rangeDays]);

  const spansMultipleYears =
    formattedData.length > 0 &&
    formattedData[0].weekKey.slice(0, 4) !==
      formattedData[formattedData.length - 1].weekKey.slice(0, 4);
  const formatWeekLabel = (weekKey: string) =>
    format(
      parseLocalDate(weekKey),
      spansMultipleYears ? "MMM d, yyyy" : "MMM d",
    );

  const ticks =
    formattedData.length > 0
      ? [
          formattedData[0].weekKey,
          formattedData[formattedData.length - 1].weekKey,
        ]
      : [];

  // A single week of data can't form a line, so render dots in that case to
  // keep the point visible.
  const showDots = points.length < 2;

  return (
    <div className={containerSpacing}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <Text type={compact ? "P2" : "H3"} fontWeight="SemiBold">
            {showMentions ? "Mention trend" : "Star rating trend"}
          </Text>
          <div className="flex gap-1">
            {RANGE_OPTIONS.map((option) => (
              <Button
                key={option.days}
                size="xs"
                variant={rangeDays === option.days ? "secondary" : "ghost"}
                onClick={() => setRangeDays(option.days)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {loading || resolvingEmptyState ? (
          <Skeleton className={cn("w-full rounded-lg", chartHeight)} />
        ) : !hasData ? (
          <EmptyState>
            <EmptyStateTitle>
              {hasDataOutsideRange
                ? showMentions
                  ? `No mentions in the last ${rangeDays} days`
                  : `No reviews in the last ${rangeDays} days`
                : showMentions
                  ? "No mentions yet"
                  : "No rating data yet"}
            </EmptyStateTitle>
            <EmptyStateDescription>
              {hasDataOutsideRange
                ? showMentions
                  ? "Select a longer range to see earlier mentions of this theme."
                  : "Select a longer range to see earlier reviews."
                : showMentions
                  ? "Theme mentions will appear here once guest feedback is imported."
                  : "Star ratings will appear here once guest feedback is imported."}
            </EmptyStateDescription>
          </EmptyState>
        ) : (
          <ChartContainer
            config={CHART_CONFIG}
            className={cn("w-full", chartHeight)}
          >
            <LineChart
              accessibilityLayer
              data={formattedData}
              margin={{ top: 10, right: 10, bottom: 0, left: 12 }}
            >
              <CartesianGrid
                vertical={false}
                horizontal
                strokeDasharray="6 6"
              />
              <XAxis
                dataKey="weekKey"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                ticks={ticks}
                tickFormatter={formatWeekLabel}
                allowDuplicatedCategory={false}
                type="category"
                interval="preserveStartEnd"
              />
              {showMentions ? (
                <YAxis
                  orientation="left"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  width={56}
                  allowDecimals={false}
                  label={{
                    value: "Mentions",
                    angle: -90,
                    position: "insideLeft",
                    offset: 0,
                    className: "fill-muted-foreground text-xs",
                    style: { textAnchor: "middle" },
                  }}
                />
              ) : (
                <YAxis
                  orientation="left"
                  domain={["dataMin - 0.5", "dataMax + 0.5"]}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  width={56}
                  tickFormatter={(v: number) => v.toFixed(1)}
                  label={{
                    value: "Avg Rating",
                    angle: -90,
                    position: "insideLeft",
                    offset: 0,
                    className: "fill-muted-foreground text-xs",
                    style: { textAnchor: "middle" },
                  }}
                />
              )}
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) =>
                      `Week of ${format(parseLocalDate(String(label)), "MMM d, yyyy")}`
                    }
                    formatter={(value, _name, item) => {
                      if (item.dataKey === "count") {
                        return `${Number(value)} mention${Number(value) !== 1 ? "s" : ""}`;
                      }
                      return `${Number(value).toFixed(1)} avg rating`;
                    }}
                  />
                }
                isAnimationActive={false}
              />
              {showMentions ? (
                <Line
                  dataKey="count"
                  type="monotone"
                  strokeWidth={2}
                  dot={showDots}
                  stroke="#7c3aed"
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  dataKey="avgRating"
                  type="monotone"
                  strokeWidth={2}
                  dot={showDots}
                  stroke="#4778F5"
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ChartContainer>
        )}
      </div>
    </div>
  );
};

export default GuestFeedbackRatingTrendChart;

gql`
  query GuestFeedbackRatingTrend($input: GuestFeedbackRatingTrendInput!) {
    GuestFeedbackRatingTrend(input: $input) {
      weekStart
      avgRating
      count
    }
  }
`;
